import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";
import { photoUrl } from "../../lib/usePortfolio";
import SortableGrid from "./SortableGrid";
import StoryEditor from "./StoryEditor";

const miniBtn = "inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[9px] font-mono tracking-[0.15em] uppercase transition-colors";

export default function AdminJournal({ onLogout }) {
  const [stories, setStories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["journal"] });

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([adminApi.get("/journal/all"), adminApi.get("/portfolio/all")]);
      setStories(s.data);
      setPhotos(p.data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upsert = (data) => {
    setStories((list) => (list.some((s) => s.id === data.id) ? list.map((s) => (s.id === data.id ? data : s)) : [...list, data]));
    invalidate();
  };

  const togglePublish = async (s) => {
    try {
      const { data } = await adminApi.patch(`/journal/${s.id}`, { published: !s.published });
      upsert(data);
      toast.success(data.published ? "Story published" : "Story hidden");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/journal/${id}`);
      setStories((list) => list.filter((s) => s.id !== id));
      invalidate();
      toast.success("Story deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reorder = async (ids) => {
    const byId = Object.fromEntries(stories.map((s) => [s.id, s]));
    setStories(ids.map((id) => byId[id]));
    try {
      await adminApi.put("/journal/order", { ids });
      invalidate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  if (editing !== null) {
    return (
      <StoryEditor
        story={editing}
        photos={photos}
        onSaved={(data) => { upsert(data); setEditing(null); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div data-testid="admin-journal-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">Featured Weddings</p>
          <h2 className="mt-2 font-serif text-xl text-ivory">{stories.length} stor{stories.length === 1 ? "y" : "ies"} · {stories.filter((s) => s.published).length} live</h2>
        </div>
        <button data-testid="journal-new-button" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-champagne text-obsidian px-6 py-3 text-[11px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors">
          <Plus size={14} /> New Story
        </button>
      </div>

      {loading && stories.length === 0 ? (
        <p data-testid="journal-admin-loading" className="mt-16 text-[11px] font-mono tracking-[0.3em] uppercase text-stone-500 animate-pulse">Loading journal…</p>
      ) : stories.length === 0 ? (
        <p data-testid="journal-admin-empty" className="mt-16 font-serif text-xl italic text-stone-500">No stories yet — write your first featured wedding.</p>
      ) : (
        <>
          <p className="mt-8 mb-4 text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">Drag the grip to set the order shown on the site</p>
          <SortableGrid
            items={stories}
            onReorder={reorder}
            testId="journal-admin-list"
            className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4"
            renderItem={(s) => (
              <article data-testid={`journal-card-${s.id}`} className="border border-white/10 bg-elevated overflow-hidden h-full">
                <div className="relative aspect-[16/10] bg-surface overflow-hidden">
                  {s.cover ? <img src={photoUrl(s.cover)} alt={s.title} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center font-serif italic text-stone-600">No cover</div>}
                  <span data-testid={`journal-status-${s.id}`} className={`absolute top-2 left-2 px-2 py-1 text-[9px] font-mono tracking-[0.2em] uppercase ${s.published ? "bg-gold text-obsidian" : "bg-black/70 text-stone-400"}`}>
                    {s.published ? "Live" : "Draft"}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-[9px] font-mono tracking-[0.2em] uppercase text-champagne truncate">{[s.couple, s.venue].filter(Boolean).join(" · ") || "—"}</p>
                  <h3 className="mt-1 font-serif text-base text-ivory leading-tight truncate">{s.title}</h3>
                  <p className="mt-1 text-[10px] font-mono text-stone-500">{s.photos.length} frames · /journal/{s.slug}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    <button data-testid={`journal-edit-${s.id}`} onClick={() => setEditing(s)} className={`${miniBtn} border-white/15 text-stone-300 hover:border-champagne hover:text-champagne`}><Pencil size={11} /> Edit</button>
                    <button data-testid={`journal-toggle-${s.id}`} onClick={() => togglePublish(s)} className={`${miniBtn} border-white/15 text-stone-300 hover:border-gold hover:text-gold`}>
                      {s.published ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Publish</>}
                    </button>
                    {s.published && (
                      <Link to={`/journal/${s.slug}`} data-testid={`journal-view-${s.id}`} className={`${miniBtn} border-white/15 text-stone-300 hover:border-champagne hover:text-champagne`}><ExternalLink size={11} /> View</Link>
                    )}
                    {confirmId === s.id ? (
                      <button data-testid={`journal-delete-confirm-${s.id}`} onClick={() => remove(s.id)} className={`${miniBtn} border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian`}>Confirm</button>
                    ) : (
                      <button data-testid={`journal-delete-${s.id}`} onClick={() => setConfirmId(s.id)} className={`${miniBtn} border-white/15 text-stone-500 hover:border-terracotta hover:text-terracotta`} aria-label="Delete story"><Trash2 size={11} /></button>
                    )}
                  </div>
                </div>
              </article>
            )}
          />
        </>
      )}
    </div>
  );
}
